import { collection, addDoc, getDocs, query, where, serverTimestamp, doc, updateDoc, getDoc } from 'firebase/firestore';
import { firestore, assertFirebaseReady } from '../firebaseConfig';

/**
 * 1:1 직접 메시지를 전송하는 함수
 * @param {string} chatRoomName - 채팅방 이름
 * @param {string} senderId - 발신자 ID
 * @param {string} senderName - 발신자 이름
 * @param {string} receiverId - 수신자 ID
 * @param {string} messageText - 메시지 내용
 * @param {string} messageType - 메시지 타입 ('text' 또는 'image')
 * @param {array} imageData - 이미지 데이터 (이미지 메시지인 경우)
 * @param {object} options - roomKey/context/student metadata
 * @returns {Promise<object>} 전송 결과
 */
export const sendDirectMessage = async (chatRoomName, senderId, senderName, receiverId, messageText, messageType = 'text', imageData = null, options = {}) => {
    try {
        assertFirebaseReady('채팅 기능');

        if (!senderId || !receiverId || !messageText) {
            throw new Error('필수 매개변수가 누락되었습니다.');
        }

        const chatRoomId = await findOrCreateChatRoom(chatRoomName, senderId, senderName, receiverId, options);

        const messageData = {
            senderId: senderId,
            senderName: senderName,
            text: messageText,
            messageType: messageType,
            timestamp: serverTimestamp(),
            chatroomId: chatRoomId
        };

        if (messageType === 'image' && imageData) {
            messageData.images = imageData;
        }

        const messagesRef = collection(firestore, `chatRooms/${chatRoomId}/messages`);
        const messageDoc = await addDoc(messagesRef, messageData);

        try {
            const chatroomRef = doc(firestore, 'chatRooms', chatRoomId);
            const chatroomDoc = await getDoc(chatroomRef);

            if (chatroomDoc.exists()) {
                const updateData = {
                    lastMessage: messageText,
                    lastMessageTime: serverTimestamp(),
                    [`lastReadTimestamp.${senderId}`]: Date.now()
                };

                await updateDoc(chatroomRef, updateData);
            }
        } catch (updateError) {
            console.error('채팅방 정보 업데이트 오류:', updateError);
        }

        console.log('✅ 메시지 전송 완료:', messageDoc.id);

        return {
            success: true,
            messageId: messageDoc.id,
            chatRoomId: chatRoomId
        };
    } catch (error) {
        console.error('메시지 전송 실패:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * 두 사용자 간의 1:1 채팅방을 찾거나 생성
 */
const findOrCreateChatRoom = async (chatRoomName, senderId, senderName, receiverId, options = {}) => {
    try {
        assertFirebaseReady('채팅 기능');

        const participantKey = [senderId, receiverId].sort().join('_');
        const contextKey = options?.contextKey ? String(options.contextKey) : '';
        const roomKey = options?.roomKey || (contextKey ? `${participantKey}_${contextKey}` : participantKey);
        const participants = Array.from(new Set([senderId, receiverId]));

        const q = query(
            collection(firestore, 'chatRooms'),
            where('roomKey', '==', roomKey)
        );

        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            return querySnapshot.docs[0].id;
        }

        const chatroomData = {
            type: 'direct',
            roomKey,
            name: options?.roomName || chatRoomName,
            participants,
            createdBy: senderId,
            createdByName: senderName,
            targetRole: options?.targetRole || 'student',
            contextType: options?.contextType || '',
            contextId: options?.contextId || '',
            studentId: options?.studentId || '',
            studentName: options?.studentName || '',
            createdAt: serverTimestamp(),
            lastMessage: '',
            lastMessageTime: serverTimestamp(),
            unreadCount: participants.reduce((acc, userId) => {
                acc[userId] = 0;
                return acc;
            }, {}),
            lastReadTimestamp: participants.reduce((acc, userId) => {
                acc[userId] = 0;
                return acc;
            }, {})
        };

        const docRef = await addDoc(collection(firestore, 'chatRooms'), chatroomData);
        console.log('✅ 새 채팅방 생성:', docRef.id);

        return docRef.id;
    } catch (error) {
        console.error('채팅방 찾기/생성 실패:', error);
        throw error;
    }
};

/**
 * 그룹 채팅방 생성 함수
 */
export const createGroupChatRoom = async (title, participants, createdBy, createdByName) => {
    try {
        assertFirebaseReady('채팅 기능');

        const normalizedParticipants = Array.from(new Set(participants));

        if (normalizedParticipants.length < 2) {
            throw new Error('그룹 채팅방은 최소 2명 이상이어야 합니다.');
        }

        const chatroomData = {
            type: 'group',
            title,
            participants: normalizedParticipants,
            createdBy,
            createdByName,
            createdAt: serverTimestamp(),
            lastMessage: '',
            lastMessageTime: serverTimestamp(),
            unreadCount: normalizedParticipants.reduce((acc, userId) => {
                acc[userId] = 0;
                return acc;
            }, {}),
            lastReadTimestamp: normalizedParticipants.reduce((acc, userId) => {
                acc[userId] = 0;
                return acc;
            }, {})
        };

        const docRef = await addDoc(collection(firestore, 'chatRooms'), chatroomData);
        return docRef.id;
    } catch (error) {
        console.error('그룹 채팅방 생성 실패:', error);
        throw error;
    }
};
